import moment from 'moment';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { generateTermOptions } from '../../PriceAndFinanceDetails/service/utils';

/**
 * Formats a date range as a string in the format "ddd, DD MMM YYYY - ddd, DD MMM YYYY".
 *
 * @param {string} startDateStr - The start date in the format 'MM/DD/YYYY'.
 * @param {string} endDateStr - The end date in the format 'MM/DD/YYYY'.
 * @returns {string} - The formatted date range string, or an empty string if either input is undefined.
 */
export const formatDateRange = (startDateStr, endDateStr) => {
  if (startDateStr === undefined || endDateStr === undefined) {
    return '';
  }
  const startDate = moment(startDateStr, 'MM/DD/YYYY');
  const endDate = moment(endDateStr, 'MM/DD/YYYY');

  const formattedStartDate = startDate.format('ddd, DD MMM YYYY');
  const formattedEndDate = endDate.format('ddd, DD MMM YYYY');

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const getPaymentSummary = ({ priceDetails, summaryDetails, t, financeOptions, language }) => {
  // map subItems
  const subDownPayment = summaryDetails?.dueUponDeliveryGroup?.dueOnDeliverySubItemsGroup?.items?.[0]?.value || 0;
  const subDueUponDelivery = summaryDetails?.dueUponDeliveryGroup?.dueOnDeliverySubItemsGroup?.items?.[1]?.value || 0;

  return [
    {
      key: 'estimatedBalance',
      label: t('Sub-apps.PriceAndFinance.estimatedBalanceLabel'),
      value: formatPrice(priceDetails?.monthly?.estBalance, language, 2),
      displayOrder: 0,
    },
    {
      key: 'termByMonth',
      label: t('Sub-apps.PriceAndFinance.termByMonthLabel'),
      value: generateTermOptions(priceDetails?.monthly?.termOptions),
      displayOrder: 1,
    },
    {
      key: 'apr',
      label: t('Sub-apps.PriceAndFinance.aprLabel'),
      value: `${summaryDetails?.financeAnnualFinanceRate?.value}%`,
      displayOrder: 2,
    },
    {
      key: 'downPayment',
      label: t('Sub-apps.PriceAndFinance.downPaymentLabel'),
      value: formatPrice(priceDetails?.monthly?.downPayment, language, 2),
      displayOrder: 3,
    },
    {
      key: 'amountToBeFinanced',
      label: t('Sub-apps.PriceAndFinance.amountToBeFinancedLabel'),
      value: formatPrice(summaryDetails?.financeAmountAmortised?.value, language, 2),
      displayOrder: 4,
    },
    {
      key: 'totalWeeklyPayment',
      label: t(`Sub-apps.PriceAndFinance.total${financeOptions}PaymentLabel`),
      value: formatPrice(summaryDetails?.financeTotalMonthlyPayment?.value, language, 2),
      displayOrder: 5,
    },
    {
      key: 'weeklyPayments',
      label: t(`Sub-apps.PriceAndFinance.numberOf${financeOptions}PaymentsLabel`),
      value: summaryDetails?.financeTotalOfWeeklyPayments?.value,
      displayOrder: 6,
    },
    {
      key: 'totalOfWeeklyPayments',
      label: t(`Sub-apps.PriceAndFinance.totalOf${financeOptions}PaymentsLabel`),
      value: formatPrice(summaryDetails?.financeTotalofMonthlyPayments?.value, language, 2),
      displayOrder: 7,
    },
    {
      key: 'totalCostOfBorrowing',
      label: t('Sub-apps.PriceAndFinance.costOfBorrowingLabel'),
      value: formatPrice(priceDetails?.monthly?.totalCostOfBorrowing, language, 2),
      displayOrder: 8,
    },
    {
      key: 'dueUponDelivery',
      label: t('Sub-apps.PriceAndFinance.dueUponDeliveryLabel'),
      value: '',
      displayOrder: 9,
    },
    {
      key: 'downPaymentField',
      label: t('Sub-apps.PriceAndFinance.downPaymentLabel'),
      value: formatPrice(subDownPayment, language, 2),
      displayOrder: 10,
    },
    {
      key: 'dueUponDeliveryField',
      label: t('Sub-apps.PriceAndFinance.dueUponDeliveryLabel'),
      value: formatPrice(subDueUponDelivery, language, 2),
      displayOrder: 11,
    },
    {
      key: 'offerValidThrough',
      label: t('Sub-apps.PriceAndFinance.offerValidThroughLabel'),
      value: formatDateRange(priceDetails?.monthly?.ratePeriodStart, priceDetails?.monthly?.ratePeriodEnd),
      displayOrder: 12,
    },
  ];
};

export const convertKeysToLowerCase = obj => {
  if (obj === null) return null;
  const json = JSON.stringify(obj);
  const newJson = json?.replace(/"([\w]+)":/g, (i, key) => `"${key?.toLowerCase()}":`);
  return newJson ? JSON.parse(newJson) : null;
};

export const getFinanceLevisDetails = (registeringFees, summaryDetails, language) =>
  registeringFees?.keys?.map((key, labelIndex) => {
    const labelField = key?.toLowerCase()?.replace(/_/g, '');
    const summaryDetailsTransformed = convertKeysToLowerCase(summaryDetails);

    return {
      key: key?.replace(/ /g, ''),
      label: summaryDetailsTransformed?.[`financelevy${labelField}`]?.title,
      value: formatPrice(summaryDetailsTransformed?.[`financelevy${labelField}`]?.value, language, 2),
      displayOrder: labelIndex,
    };
  }) || [];

export const combinePaymentAndSummary = (paymentSummary, financeLevies) => {
  const maxDisplayOrderArray1 = Math.max(...financeLevies.map(item => item.displayOrder));
  const startingDisplayOrderArray2 = maxDisplayOrderArray1 + 1;

  // Update consecutive displayOrder
  paymentSummary.forEach((item, index) => {
    item.displayOrder = startingDisplayOrderArray2 + index;
  });

  return [...financeLevies, ...paymentSummary];
};
